import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Eraser, Ban, CalendarPlus } from "lucide-react";

const KEEP = ["Elenco dei corsi", "Docenti assegnati ai corsi", "Testo e campi del modulo di iscrizione", "Testo della privacy", "Notifiche, promemoria e testi delle email"];
const EMPTY = ["Prezzi (rata unica e rate)", "Date della seconda rata"];
const RESET = ["Data del saggio di ogni corso", "Chiusure", "Codice di iscrizione"];
const NOT = ["Date di prova", "Prenotazioni di prova", "Calendario delle lezioni", "Blocchi/conferme del calendario", "Iscrizioni", "Pagamenti", "Moduli firmati", "Tessere ARCI", "Note"];

const Group = ({ icon: Icon, title, items, testid }) => (
  <div data-testid={testid}>
    <p className="text-sm font-semibold flex items-center gap-1.5"><Icon size={14} /> {title}</p>
    <ul className="mt-1 text-sm text-muted-foreground list-disc pl-5">{items.map((x) => <li key={x}>{x}</li>)}</ul>
  </div>
);

export const CreateYearDialog = ({ open, onOpenChange, years, currentYear, onCreated }) => {
  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // predefinito: l'anno più recente
  const src = source || years[0] || "";

  const reset = (o) => { if (!o) { setName(""); setSource(""); setPreview(null); } onOpenChange(o); };
  const doPreview = async () => {
    setBusy(true);
    try { setPreview((await api.post("/admin/years/preview", { name: name.trim(), source: src })).data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/years", { name: name.trim(), source: src });
      toast.success(`Anno ${data.year} creato`); onCreated(data); reset(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent data-testid="create-year-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Crea nuovo anno</DialogTitle>
          <DialogDescription>Il nuovo anno è una copia indipendente: non diventa l'anno corrente e gli allievi non lo vedono.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Nome del nuovo anno</Label>
            <Input data-testid="create-year-name" value={name} onChange={(e) => { setName(e.target.value); setPreview(null); }} placeholder="2027/2028" className="mt-1 bg-white" /></div>
          <div><Label className="text-xs">Copia dall'anno</Label>
            <Select value={src} onValueChange={(v) => { setSource(v); setPreview(null); }}>
              <SelectTrigger data-testid="create-year-source" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {preview && (
          <div className="space-y-3 rounded-lg border border-border p-3" data-testid="create-year-preview">
            <div>
              <p className="text-sm font-semibold flex items-center gap-1.5"><Copy size={14} /> Copiati identici</p>
              <ul className="mt-1 text-sm text-muted-foreground list-disc pl-5">
                {KEEP.map((x) => <li key={x}>{x}</li>)}
              </ul>
              {preview.courses.length === 0 ? <p className="text-sm mt-1">Nessun corso nell'anno {preview.source}.</p> : (
                <ul className="mt-2 text-sm space-y-0.5" data-testid="create-year-courses">
                  {preview.courses.map((c) => <li key={c.course} className="break-words"><strong>{c.course}</strong> · {c.teacher_name || "docente non indicato"}</li>)}
                </ul>
              )}
            </div>
            <Group icon={Eraser} title="Vuoti, da impostare" items={EMPTY} testid="create-year-empty" />
            <Group icon={Eraser} title="Azzerati" items={RESET} testid="create-year-reset" />
            <Group icon={Ban} title="Non copiati" items={NOT} testid="create-year-not" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => reset(false)}>Annulla</Button>
          {!preview
            ? <Button data-testid="create-year-preview-btn" disabled={busy || !name.trim() || !src} onClick={doPreview}>Anteprima</Button>
            : <Button data-testid="create-year-confirm" disabled={busy} onClick={create}><CalendarPlus size={16} className="mr-1.5" /> Crea anno {preview.name}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
